import { Brand, FeatureFlag } from '@ps/web-core-enums';
import { useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';

import { Country } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';
import type { Store } from '@swf/core/store';
import type { WizardContext } from '@swf/core/types/Wizard';

import type { RealMoneyInformation } from '../../../types/Cashier';
import { CashierFlowType } from '../../../types/Cashier';
import type { FeatureFlagData } from '../../Signup/hooks/useSignupGuards';

export enum Guard {
  CheckRMSuspended = 'checkRMSuspended',
  CheckUserFlags = 'checkUserFlags',
  DoesJumpToFeatureStep = 'doesJumpToFeatureStep',
  PhoneVerificationCheck = 'phoneVerificationCheck',
  RmfChallengeQuestionsCheck = 'rmfChallengeQuestionsCheck',
  RmfDobEnabled = 'rmfDobEnabled',
  ShowPendingVerification = 'showPendingVerification',
  ShowItBonusOffer = 'ShowItBonusOffer',
  ShowWelcomeBonus = 'showWelcomeBonus',
  SignupDepositEnabled = 'signupDepositEnabled',
  PhoneCollectionCheck = 'phoneCollectionCheck',
  GoToDepositLimits = 'goToDepositLimits',
  LimitsSelected = 'limitsSelected',
  SingleSignUpEnabled = 'singleSignUpEnabled',
  ShowSumSubFirst = 'sumSubFirst',
  CheckSumSubVerification = 'checkSumSubVerification',
  isCashierPath = 'isCashierPath',
  recheckUserFlags = 'recheckUserFlags',
  isRetryScreenEnabled = 'isRetryScreenEnabled',
  checkPoker = 'checkPoker',
  checkCasino = 'checkCasino'
}

const useRMFGuards = () => {
  const store = useStore() as Store;
  const isSWCSignupDepositEnabled = useFeature(FeatureFlag.SwcSignupDeposit);
  const isSWCWelcomeBonusScreenEnabled = useFeature(FeatureFlag.SwcWelcomeBonusScreen);
  const isSWCAffiliateBonusEnabled = useFeature(FeatureFlag.SwcAffiliateBonus);
  const isSumSubEnabled = useFeature(FeatureFlag.SwcSignupRmfSumSub);
  const isRetryScreenEnabled = useFeature(FeatureFlag.SwcRetryScreen);
  const hideBonusCode = useSelector(select.account.hideBonusCode);
  const {
    data: { cashierFlow }
  } = useWizardContext();

  const doesJumpToFeatureStep = useCallback(
    ({
      data: { cashierFlow, depositRmfRequired }
    }: {
      data: { cashierFlow: CashierFlowType; depositRmfRequired: boolean };
    }): boolean =>
      [CashierFlowType.FirstWithdraw, CashierFlowType.FirstP2pTransfer].includes(cashierFlow) ||
      depositRmfRequired,
    []
  );

  const rmfDobEnabled = useCallback(
    ({
      data: {
        userInfo: { dateOfBirth }
      }
    }: {
      data: { userInfo: { dateOfBirth: string } };
    }) => !dateOfBirth,
    []
  );

  const rmfChallengeQuestionsCheck = useCallback(
    ({
      data: { realMoneyInformation }
    }: {
      data: { realMoneyInformation: RealMoneyInformation | null };
    }) => !!realMoneyInformation?.securityQuestions,
    []
  );

  const showItBonusOffer = useCallback(
    ({ data: { signupPassed } }: { data: { signupPassed: boolean } }) => {
      const rootState = store.getState();
      const { registrationBrand } = select.localSettings.get(rootState);
      return registrationBrand !== Brand.BetStars && signupPassed;
    },
    [store]
  );

  const showWelcomeBonus = useCallback(
    ({
      data: { crmBonusCode, signupPassed }
    }: {
      data: {
        crmBonusCode: string;
        signupPassed: boolean;
      };
    }): boolean =>
      isSWCWelcomeBonusScreenEnabled &&
      !!(isSWCAffiliateBonusEnabled || crmBonusCode) &&
      !!signupPassed &&
      !hideBonusCode,
    [hideBonusCode, isSWCAffiliateBonusEnabled, isSWCWelcomeBonusScreenEnabled]
  );

  const signupDepositEnabled = () => isSWCSignupDepositEnabled;

  const checkUserFlags = useCallback(() => {
    const rootState = store.getState();
    const isKYCRequired = store.select.account.isKYCRequired(rootState);
    const isAuthVerified = store.select.account.isAuthVerified(rootState);
    const isAgeVerified = store.select.account.isAgeVerified(rootState);
    const isRealMoneyOK = store.select.account.isRealMoneyOK(rootState);
    return (
      isKYCRequired || (!isKYCRequired && (!isAuthVerified || !isAgeVerified || !isRealMoneyOK))
    );
  }, [store]);

  const showPendingVerification = useCallback(
    () => !store.select.account.isKYCRequired(store.getState()),
    [store]
  );

  const checkRMSuspended = useCallback(
    () => store.select.account.isSuspendRM(store.getState()),
    [store]
  );

  const phoneVerificationCheck = useCallback(
    ({ data: { features } }: FeatureFlagData) => !!features[FeatureFlag.SwcRMFPhoneVerification],
    []
  );

  const phoneCollectionCheck = useCallback(
    ({ data: { features } }: FeatureFlagData) => !!features[FeatureFlag.SwcSignupPhoneCollection],
    []
  );

  const singleSignUpEnabled = useCallback(
    ({ data: { features } }: FeatureFlagData) => !!features[FeatureFlag.SwcSignupOnePage],
    []
  );

  const isCashierPath = useCallback(
    ({ data: { cashierFlow } }: { data: { cashierFlow: CashierFlowType } }) =>
      [CashierFlowType.FirstDeposit, CashierFlowType.FirstWithdraw].includes(cashierFlow),
    []
  );

  const showLimitsScreen = useCallback(
    ({ values: { userLimitsSelect } }: Pick<WizardContext, 'values'>) => userLimitsSelect.skipped,
    []
  );

  const showSumSubFirst = useCallback(
    ({ values: { country } }: Pick<WizardContext, 'values'>) =>
      country === Country.Brazil && isSumSubEnabled,
    [isSumSubEnabled]
  );

  const checkSumSubVerification = useCallback(
    ({ values: { sumSubVerification } }: Pick<WizardContext, 'values'>) =>
      sumSubVerification.pased && (!CashierFlowType.FirstWithdraw.includes(cashierFlow) as boolean),
    [cashierFlow]
  );

  const retryScreenEnabled = useCallback(() => isRetryScreenEnabled, [isRetryScreenEnabled]);

  const checkCasino = useCallback(() => {
    const rootState = store.getState();
    const { registrationBrand } = select.localSettings.get(rootState);
    return registrationBrand === Brand.PokerStarsCasino;
  }, [store]);

  const checkPoker = useCallback(() => {
    const rootState = store.getState();
    const { registrationBrand } = select.localSettings.get(rootState);
    return !registrationBrand || registrationBrand === Brand.PokerStars;
  }, [store]);

  return {
    [Guard.CheckRMSuspended]: checkRMSuspended,
    [Guard.CheckUserFlags]: checkUserFlags,
    [Guard.DoesJumpToFeatureStep]: doesJumpToFeatureStep,
    [Guard.PhoneVerificationCheck]: phoneVerificationCheck,
    [Guard.RmfChallengeQuestionsCheck]: rmfChallengeQuestionsCheck,
    [Guard.RmfDobEnabled]: rmfDobEnabled,
    [Guard.ShowPendingVerification]: showPendingVerification,
    [Guard.ShowWelcomeBonus]: showWelcomeBonus,
    [Guard.ShowItBonusOffer]: showItBonusOffer,
    [Guard.SignupDepositEnabled]: signupDepositEnabled,
    [Guard.PhoneCollectionCheck]: phoneCollectionCheck,
    [Guard.LimitsSelected]: showLimitsScreen,
    [Guard.SingleSignUpEnabled]: singleSignUpEnabled,
    [Guard.ShowSumSubFirst]: showSumSubFirst,
    [Guard.CheckSumSubVerification]: checkSumSubVerification,
    [Guard.isCashierPath]: isCashierPath,
    [Guard.recheckUserFlags]: checkUserFlags,
    [Guard.isRetryScreenEnabled]: retryScreenEnabled,
    [Guard.checkPoker]: checkPoker,
    [Guard.checkCasino]: checkCasino
  };
};

export default useRMFGuards;
